import { AfterViewInit, Component, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSelect } from '@angular/material/select';
import { ActivatedRoute } from '@angular/router';
import { first, tap } from 'rxjs';
import { indicate } from 'src/app/operators';
import { DictionaryItem, DictionaryItemDefinition, DictionarySimplePageComponent } from 'src/app/shared/components/dictionary-simple-page/dictionary-simple-page.component';
import { ComponentPropertyViewModel, EsraPipingAPIClient } from 'src/app/shared/models/autogenerated-piping';
import { formatComponentPropertyValue } from 'src/app/shared/models/material-astm.model';
import { Constants } from 'src/app/shared/constants/constants';
import { UserService } from 'src/app/core/services/user.service';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { DictionaryExportService } from '../services/dictionary-export.service';
import {
  ComponentPropertyAddEditComponent,
  ComponentPropertyEditorData
} from '../component-property-add-edit/component-property-add-edit.component';


@Component({
    selector: 'app-component-properties',
    templateUrl: '../../../../shared/components/dictionary-simple-page/dictionary-simple-page.component.html',
    styleUrls: ['../../../../shared/components/dictionary-simple-page/dictionary-simple-page.component.scss',
    ],
    standalone: false
})
export class ComponentPropertiesComponent extends DictionarySimplePageComponent<ComponentPropertyViewModel> implements AfterViewInit {
  @ViewChild('SelectGroup') SelectGroup: MatSelect;
  @ViewChild('SelectType') SelectAttribute: MatSelect;
  selectedGroup: string | undefined = undefined;
  selectedAttributeName: string | undefined = undefined;


  private fetchNonValves: boolean;
  private fetchValves: boolean;

  private get isValveList(): boolean { return this.fetchValves && !this.fetchNonValves; }
  private get isComponentList(): boolean { return !this.fetchValves && this.fetchNonValves; }
  private get dictionaryType(): "ComponentProperty" | "Valve" | "" {
    if (this.isValveList) { return "Valve"; }
    if (this.isComponentList) { return "ComponentProperty"; }
    return "";
  }

  constructor(
    private readonly esraApiClient: EsraPipingAPIClient,
    private readonly route: ActivatedRoute,
    private readonly dialog: MatDialog,
    private readonly userService: UserService,
    private readonly snackbarService: SnackBarService,
    private readonly dictionaryExportService: DictionaryExportService
  ) {
    super();
  }

  childOnInitBefore(): void {
    this.typeFilterPlaceholder = 'Sort by attribute';
    this.typeFilterAriaLabel = 'Select an Attribute';

    this.route.data.subscribe((data: { fetchValves: boolean, fetchNonValves: boolean }) => {
      this.fetchNonValves = data.fetchNonValves;
      this.fetchValves = data.fetchValves;
      // Wire the correct export endpoint based on whether this instance is for valves or components
      this.exportFn = this.fetchValves && !this.fetchNonValves
        ? () => this.dictionaryExportService.exportValveProperties()
        : () => this.dictionaryExportService.exportComponentProperties();
    });
  }

  protected childOnInitAfter(): void { /* do nothing */ }

  ngAfterViewInit(): void {
    this.getComponentGroups();
  }

  protected initializeDictionaryDefinition(): DictionaryItemDefinition<ComponentPropertyViewModel> {
    return {
      displayStatusColumn: true,
      displayDeleteButton: false,
      dictionaryItems: [
        new DictionaryItem('Attribute', 'name', 'standard', (item: ComponentPropertyViewModel) => this.replaceUnderscore(item)),
        new DictionaryItem('Value', 'value', 'extended',
          (item: ComponentPropertyViewModel) => formatComponentPropertyValue(item.value, item.dataType)),
        new DictionaryItem('Code', 'code', 'standard')
      ],
    };
  }

  override searchAll(): void {
    this.esraApiClient
      .getAllComponentProperties("", "", this.fetchNonValves, this.fetchValves, undefined, false)
      .pipe(
        indicate(this.searching$),
        first(),
        tap((componentProperties) => {
          this.totalCount = componentProperties.length;
          this.dataSource.data = componentProperties;
        })
      )
      .subscribe();
  }

  getComponentGroups(): void {
    this.esraApiClient.componentGroups()
      .pipe(
        first()
      )
      .subscribe(
        (groups: string[]) => {
          if (!this.fetchValves) {
            groups = groups.filter(group => group !== 'Valves');
          }
          if (!this.fetchNonValves) {
            groups = groups.filter(group => group === 'Valves');
          }

          this.groupData = groups;
        }
      );
  }

  getFilteredComponentProperties(): void {
    this.esraApiClient
      .getAllComponentProperties(this.selectedGroup, this.selectedAttributeName, this.fetchNonValves, this.fetchValves, undefined, false)
      .pipe(
        indicate(this.searching$),
        first(),
        tap((componentProperties) => {
          this.totalCount = componentProperties.length;
          this.dataSource.data = componentProperties;
        })
      )
      .subscribe();
  }

  override selectionChange(event: any, type: string): void {
    if (type === 'group') {
      this.selectedGroup = event.value;
      this.selectedAttributeName = undefined;
      this.getAttributeNamesByGroup(this.selectedGroup);
    } else if (type === 'type') {
      this.selectedAttributeName = event.value;
    }
    this.getFilteredComponentProperties();
  }

  override clearAll(): void {
    this.SelectGroup.value = '';
    this.SelectAttribute.value = '';
    this.selectedGroup = undefined;
    this.selectedAttributeName = undefined;
    this.typeData = [];
    this.currentPage = 0;
    this.clearSearchText();
    this.searchAll();
  }

  getAttributeNamesByGroup(groupName: string): void {
    this.esraApiClient.getAttributeNamesByGroup(groupName).pipe(first()).subscribe({
      next: (attributeNames: string[]) => {
        this.typeData = attributeNames;
      },
      error: () => {
        this.snackbarService.showError('Error fetching attribute names by group');
      }
    });
  }

  protected replaceUnderscore(item: ComponentPropertyViewModel) {
    return item.name.replace("_", " ");
  }

  protected getFilterPredicate(record: ComponentPropertyViewModel, parametersMap: Map<string, string>): boolean {
    const searchQuery = parametersMap.get(DictionarySimplePageComponent.SEARCH_QUERY_NAME)?.toLowerCase() || '';
    const matchesSearchQuery = record.name.toLowerCase().includes(searchQuery) || record.value.toLowerCase().includes(searchQuery);
    if (!matchesSearchQuery) { return false; }

    /* isActiveFilter possible values:
        - 'true' (string): show only active records
        - 'false' (string): show only inactive records (currently not used))
        - null or undefined (no filter applied, show all records)
    */
    const isActiveFilter = parametersMap.get(DictionarySimplePageComponent.IS_ACTIVE_NAME);
    if (isActiveFilter !== null && isActiveFilter !== undefined) {
      return record.isActive.valueOf().toString() === isActiveFilter; // Filter by isActive status
    }

    return true; // If no filter is applied, return true for all records
  }

  protected createNew(): void {
    this.openEditor(null);
  }

  protected editItem(item: ComponentPropertyViewModel): void {
    const componentPropertyId: string | undefined = item.id;
    if (!componentPropertyId) {
      this.snackbarService.showError('Unable to load the selected property.');
      return;
    }

    this.openEditor(componentPropertyId);
  }

  private openEditor(componentPropertyId: string | null): void {
    const canEditDictionary: boolean = this.userService.isRoleExists(Constants.pipingDictionaryWriter)
      || this.userService.isRoleExists(Constants.admin)
      || this.userService.isRoleExists(Constants.scanner);
    if (!canEditDictionary) {
      this.snackbarService.showError('You are not authorized to access this resource.');
      return;
    }

    const dialogRef: MatDialogRef<ComponentPropertyAddEditComponent, boolean> = this.dialog.open<
      ComponentPropertyAddEditComponent,
      ComponentPropertyEditorData,
      boolean
    >(ComponentPropertyAddEditComponent, {
      width: '98vw',
      maxWidth: '98vw',
      height: '98vh',
      maxHeight: '98vh',
      disableClose: true,
      data: {
        dictionaryType: this.dictionaryType,
        componentPropertyId
      }
    });

    dialogRef.afterClosed().pipe(first()).subscribe((saved: boolean | undefined) => {
      if (saved) {
        this.getFilteredComponentProperties();
      }
    });
  }
  protected editItemReferenced(item: object): void {
    throw new Error('Method not implemented.');
  }
  protected deleteItem(item: ComponentPropertyViewModel): void {
    // Delete functionality is disabled for this component
  }
  protected getUsageInformation(item: object): void {
    throw new Error('Method not implemented.');
  }
  protected getNoMatchMessage(): string {
    return 'No component property match provided search criteria';
  }
  protected updateStatus(item: ComponentPropertyViewModel): void {
    this.esraApiClient
      .setComponentPropertyStatus(item.id, item.isActive)
      .pipe(
        indicate(this.searching$),
        first(),
      )
      .subscribe(
        {
          next: () => {
            this.snackbarService.showInfo('Status updated successfully');
            this.search();
          },
          error: (e: string) => {
            item.isActive = !item.isActive; // Revert the status change
            this.snackbarService.showError(`Error updating status: ${e}`);
            this.searchAll();
          }
        }
      );
  }
}
